import { z } from 'zod';
import { policyKeySchema, policyModeSchema } from '../policies';
import { severitySchema } from '../shared/severity';
import { securityIssueSchema } from '../scans';
// v2 agent decision contracts (KIR-3071). Agent (PEP) collects Observations,
// backend (PDP) returns a DecisionPlan of Actions to execute. Mirror the Go
// types in extensions/agent/pkg/{decisions,actions} (later PRs).
// Canonical verbs the agent knows how to execute.
//
// Deliberately omitted (KIR-3071): `block_request` / `allow_request`. Those are
// request-deny/allow verbs that only mean anything on a blocking IDE hook
// (PreToolUse/UserPromptSubmit). The only policy that rides blocking hooks is
// `secret-detection`, which is a permanent local carve-out and is never
// migrated to the PDP. Every policy the PDP does decide (mcp-*, hidden-
// characters, extensions, ai-instructions, yolo-modes, cursor-database) is
// file-scan or extension-eval driven — there is no in-flight request to block.
// Re-add these (and an `ActionDeny` executor on the agent) only if a
// hook-blocking policy is ever moved server-side.
//
// `report_only` is the "surface it to the user, take no protective action" verb
// (the agent shows a notification). It covers monitor-mode detections and
// enforce detections that fall below the threshold / have no quarantine fix.
// An allow-listed finding, by contrast, needs no agent-side execution at all
// (the match is already recorded server-side), so it collapses into `noop`; the
// allow-list vs policy-off distinction is carried in the action `reason`.
export var ActionType;
(function (ActionType) {
    ActionType["ReportOnly"] = "report_only";
    ActionType["QuarantineSurgical"] = "quarantine_surgical";
    ActionType["QuarantineFull"] = "quarantine_full";
    ActionType["UninstallExtension"] = "uninstall_extension";
    ActionType["Noop"] = "noop";
})(ActionType || (ActionType = {}));
/** Explicit tuple for stable `z.enum` order (mirrors `POLICY_KEY_VALUES`). */
export const ACTION_TYPE_VALUES = [
    ActionType.ReportOnly,
    ActionType.QuarantineSurgical,
    ActionType.QuarantineFull,
    ActionType.UninstallExtension,
    ActionType.Noop,
];
export const actionTypeSchema = z.enum(ACTION_TYPE_VALUES);
// A detected issue plus the policy context (which policy fired, in what mode).
export const observationSchema = z.object({
    policyKey: policyKeySchema,
    mode: policyModeSchema,
    issue: securityIssueSchema,
    // Effective enforcement threshold for `usesSeverityThreshold` policies;
    // issues below it are reported, not actioned. Omitted otherwise.
    severityThreshold: severitySchema.optional(),
});
// One instruction for the agent. `target` = where to act; `metadata` = opaque.
export const actionSchema = z.object({
    type: actionTypeSchema,
    policyKey: policyKeySchema,
    severity: severitySchema,
    reason: z.string(),
    target: z
        .object({
        location: z.string().optional(),
        rawConfigName: z.string().optional(),
    })
        .optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// Ordered actions for a batch of observations; empty = nothing to do.
export const decisionPlanSchema = z.object({
    actions: z.array(actionSchema),
});
// Status of a single executed action, reported back by the agent.
export const actionResultStatusSchema = z.enum([
    'success',
    'failed',
    'skipped',
]);
export const actionResultSchema = z.object({
    actionType: actionTypeSchema,
    policyKey: policyKeySchema,
    status: actionResultStatusSchema,
    detail: z.string().optional(),
});
// Agent-supplied input for POST /api/v2/agent/decisions. The agent sends only
// the raw detected issue; the backend (PDP) re-derives the authoritative
// policyKey, mode, and severityThreshold from stored org/user policy state.
// Client-supplied policy context is deliberately NOT part of this schema so a
// key holder cannot request enforce-level actions while the stored mode is
// monitor/off (KIR-3071).
export const decisionObservationInputSchema = z.object({
    issue: securityIssueSchema,
});
// Request/response for POST /api/v2/agent/decisions.
export const decisionRequestSchema = z.object({
    clientVersion: z.string(),
    observations: z.array(decisionObservationInputSchema),
});
export const decisionResponseSchema = z.object({
    plan: decisionPlanSchema,
});
//# sourceMappingURL=decisions.js.map